import React, { useEffect, useRef, useState } from 'react';

const isTv = (target) => !['pos', 'tablet', 'stb', 'android_box'].includes(target.type) && (target.type === 'lgtv'
  || ['tv', 'television'].includes(target.integrationData?.equipment?.kind)
  || ['lg_webos_tv', 'sony_bravia_tv', 'samsung_tv', 'television'].includes(target.integrationData?.remoteTarget?.kind));

export default function TelevisionScreenSetup({ targets, siteId, onEquipmentChange, onOpenDeviceRemote }) {
  const generation = useRef(0);
  const [areas, setAreas] = useState({});
  const [results, setResults] = useState({});
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);
  const televisions = targets.filter((target) => target.paired && isTv(target));
  const areaKey = [...new Set(televisions.map((target) => Number(target.areaId)))].sort().join(',');
  const targetKey = televisions.map((target) => `${target.deviceId}:${target.pairedAt || ''}:${target.areaId}:${target.name}`).sort().join(',');
  const token = localStorage.getItem('token');
  useEffect(() => {
    let alive = true;
    generation.current += 1;
    setAreas({}); setResults({}); setSelected({}); setBusy(false);
    Promise.all(areaKey.split(',').filter(Boolean).map(async (areaId) => {
      const response = await fetch(`/api/areas/${areaId}/topology`, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) return [areaId, null];
      return [areaId, await response.json()];
    })).then((entries) => { if (alive) setAreas(Object.fromEntries(entries)); }).catch(() => {});
    return () => { alive = false; generation.current += 1; };
  }, [areaKey, targetKey, siteId, token]);
  if (!televisions.length) return null;
  const outcome = (target) => results[target.deviceId]?.status
    || (areas[target.areaId]?.screens?.some((screen) => Number(screen.televisionDeviceId) === Number(target.deviceId)) ? 'already_linked'
      : areas[target.areaId] ? 'create' : 'review');
  const complete = async (entries) => {
    const epoch = generation.current;
    setBusy(true);
    let changed = false;
    for (const target of entries) {
      if (epoch !== generation.current) return;
      try {
        const response = await fetch(`/api/site-control/targets/${target.deviceId}/screen`, {
          method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ siteId, areaId: target.areaId, existingScreenId: selected[target.deviceId] || undefined,
            configurationFingerprint: areas[target.areaId]?.configurationFingerprint }),
        });
        const result = await response.json();
        if (epoch !== generation.current) return;
        if (!response.ok) throw new Error(result.message || 'Review Screen setup. Pairing has been retained.');
        changed = true;
        setResults((current) => ({ ...current, [target.deviceId]: result }));
        setAreas((current) => ({ ...current, [target.areaId]: { ...current[target.areaId], configurationFingerprint: result.configurationFingerprint,
          screens: [...(current[target.areaId]?.screens || []).filter((screen) => Number(screen.id) !== Number(result.screen?.id)), result.screen].filter(Boolean) } }));
      } catch (error) { if (epoch === generation.current) setResults((current) => ({ ...current, [target.deviceId]: { status: 'review', message: error.message } })); }
    }
    if (epoch === generation.current) {
      setBusy(false);
      if (changed) onEquipmentChange?.(siteId);
    }
  };
  const allLinked = televisions.every((target) => ['already_linked', 'created', 'linked'].includes(outcome(target)));
  const linkedScreen = (target) => results[target.deviceId]?.screen
    || areas[target.areaId]?.screens?.find((screen) => Number(screen.televisionDeviceId) === Number(target.deviceId));
  return <section className="site-control-section" aria-label="Television Screen setup">
    <h3>{allLinked ? 'Screens ready' : 'Complete Screen setup'}</h3>
    <p>{allLinked ? 'Your paired televisions are saved as Screens in Virtual Matrix. No further save is needed.'
      : 'Pairing automatically creates a Screen for each television when its identity is clear. Review any remaining setup below, or link an existing unmanaged Screen.'}</p>
    <table><thead><tr><th>Television</th><th>Screen setup</th><th>Link</th></tr></thead><tbody>
      {televisions.map((target) => <tr key={target.deviceId}>
        <td>{target.name} · {target.host} · #{target.deviceId}</td>
        <td>{({ create: 'Create Screen', already_linked: 'Screen ready', created: 'Created', linked: 'Linked', review: 'Review' })[outcome(target)]}
          {results[target.deviceId]?.message && <p role="status">{results[target.deviceId].message}</p>}</td>
        <td>{linkedScreen(target)?.name && <span>{linkedScreen(target).name} · </span>}
          {onOpenDeviceRemote && ['already_linked', 'created', 'linked'].includes(outcome(target)) && <button type="button"
            onClick={() => onOpenDeviceRemote(target.deviceId, target.areaId)}>Open TV remote</button>}
          {!['already_linked', 'created', 'linked'].includes(outcome(target)) && <>
          <select aria-label={`Screen for ${target.name}`} value={selected[target.deviceId] || ''} disabled={busy}
            onChange={(event) => setSelected({ ...selected, [target.deviceId]: event.target.value })}>
            <option value="">Create Screen</option>
            {(areas[target.areaId]?.screens || []).filter((screen) => screen.displayKind === 'unmanaged' && !screen.televisionDeviceId)
              .map((screen) => <option key={screen.id} value={screen.id}>{screen.name}</option>)}
          </select>
          <button type="button" disabled={busy} onClick={() => complete([target])}>Continue setup</button>
        </>}</td>
      </tr>)}
    </tbody></table>
    {!allLinked && <button type="button" disabled={busy || !televisions.some((target) => outcome(target) === 'create')}
      onClick={() => complete(televisions.filter((target) => outcome(target) === 'create' && !selected[target.deviceId]))}>Complete Screen setup</button>}
  </section>;
}
